import type { AssetTier } from './resolveAssetTier'
import { normalizeAnswerToken } from './deriveFunnelAnswers'

/**
 * One personalization rule, authored inside a module variable's JSON as an entry of
 * its optional `personalization` array:
 *
 *   { "name": "copy_a", "matchTypes": ["CPM"], "tiers": ["T1", "T2"],
 *     "selfSchedule": true,
 *     "answers": { "operator": "AND", "conditions": [
 *       { "question": "yearsFromRetirement", "values": ["Already Retired", "1-4 years"] },
 *       { "question": "timeToImprove", "values": ["asap"] }
 *     ] },
 *     "copy": { "heading": "..." } }
 *
 * `matchTypes` is required and non-empty; `tiers` is optional (omitted = any tier);
 * `selfSchedule` is optional (omitted = any; `true`/`false` requires the session's self-schedule
 * eligibility to match); `answers` is optional (omitted = no answer gate) and is evaluated by
 * `answersMatch` below; `copy` is a partial override shallow-merged over the module's base
 * config. Rules are evaluated in array order — the first match wins.
 */
export interface PersonalizationRule {
  name?: string
  matchTypes?: unknown
  tiers?: unknown
  selfSchedule?: unknown
  answers?: unknown
  copy?: unknown
}

/** One entry of an `answers` gate's `conditions` array. Typed `unknown` -- flag-authored JSON. */
export interface AnswerCondition {
  question?: unknown
  values?: unknown
}

export interface PersonalizationContext {
  matchTypes: Set<string>
  tier?: AssetTier
  /**
   * Whether this session will be offered the self-schedule booking CTA: the matches-context
   * flag ("matched at least one self-schedulable advisor") AND not an email-link revisit.
   * Deliberately narrower than the raw flag — a revisit session hides the CTA, so its copy
   * must not promise a booking. Keep in step with useSelfSchedulingEligibility's session gates.
   */
  selfScheduleEligible: boolean
  /**
   * Normalized funnel-answer lookup: question -> the session's answers, both trimmed and
   * lowercased (see `deriveFunnelAnswers`). Required, not optional, so an evaluation site
   * cannot forget to supply it and silently match answers-bearing rules on the other gates
   * alone. An empty map just means no `answers` condition can hit.
   */
  answers: Map<string, Set<string>>
}

export interface PersonalizationResult<T> {
  config: T
  /** Name of the matched rule for analytics, or null when no rule matched. */
  matchedRuleName: string | null
}

/**
 * Evaluates a rule's `answers` gate. A condition hits when the session's answer set for
 * `question` intersects `values` -- intersection, not equality. `operator` defaults to `OR`
 * when omitted. Both sides are compared via `normalizeAnswerToken` (trim + lowercase).
 *
 * The gate is flag-authored JSON, so this fails closed -- returns `false`, never throws -- on
 * anything malformed: a non-object `answers`; an `operator` that is not `AND`/`OR` after
 * trim + uppercase; missing, non-array, or empty `conditions`; a non-object condition; a
 * missing, blank, or non-string `question`; or missing, non-array, or empty `values`.
 * A malformed condition fails the WHOLE gate, under either operator and at any position.
 * A well-formed condition the session simply has no matching answer for is NOT malformed and
 * contributes an ordinary `false` hit.
 *
 * NOTE: a non-empty `values` array with no usable string entry (e.g. `[42]`, `['   ']`) is
 * not treated as malformed -- unusable entries are skipped, the condition contributes a `false`
 * hit, and under OR a sibling's hit can still carry the gate to a match.
 *
 * Exported only so the fail-closed matrix can be unit-tested directly; the rule predicate
 * itself lives in `ruleMatches`, same as every other gate.
 */
export function answersMatch (answers: unknown, context: PersonalizationContext): boolean {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
    return false
  }
  const { operator, conditions } = answers as { operator?: unknown, conditions?: unknown }

  let normalizedOperator: 'AND' | 'OR' = 'OR'
  if (operator !== undefined) {
    if (typeof operator !== 'string') {
      return false
    }
    const upper = operator.trim().toUpperCase()
    if (upper !== 'AND' && upper !== 'OR') {
      return false
    }
    normalizedOperator = upper
  }

  if (!Array.isArray(conditions) || conditions.length === 0) {
    return false
  }

  const hits: boolean[] = []
  for (const condition of conditions) {
    if (!condition || typeof condition !== 'object' || Array.isArray(condition)) {
      return false
    }
    const { question, values } = condition as AnswerCondition
    if (typeof question !== 'string') {
      return false
    }
    const normalizedQuestion = normalizeAnswerToken(question)
    if (!normalizedQuestion) {
      return false
    }
    if (!Array.isArray(values) || values.length === 0) {
      return false
    }
    const sessionValues = context.answers.get(normalizedQuestion)
    hits.push(
      !!sessionValues &&
        values.some((value) => typeof value === 'string' && sessionValues.has(normalizeAnswerToken(value)))
    )
  }

  return normalizedOperator === 'AND' ? hits.every(Boolean) : hits.some(Boolean)
}

const ruleMatches = (rule: PersonalizationRule, context: PersonalizationContext): boolean => {
  if (!Array.isArray(rule.matchTypes) || rule.matchTypes.length === 0) {
    return false
  }
  const typeHit = rule.matchTypes.some(
    (type) => typeof type === 'string' && context.matchTypes.has(type.trim().toUpperCase())
  )
  if (!typeHit) {
    return false
  }
  if (rule.tiers !== undefined) {
    if (!Array.isArray(rule.tiers)) {
      return false
    }
    if (context.tier === undefined || !rule.tiers.includes(context.tier)) {
      return false
    }
  }
  if (rule.selfSchedule !== undefined) {
    if (typeof rule.selfSchedule !== 'boolean') {
      return false
    }
    if (rule.selfSchedule !== context.selfScheduleEligible) {
      return false
    }
  }
  if (rule.answers !== undefined) {
    if (!answersMatch(rule.answers, context)) {
      return false
    }
  }
  return true
}

/**
 * Evaluates a module config's optional `personalization` rules against the user's match types,
 * asset tier, self-schedule eligibility, and funnel answers. The first matching rule wins: its
 * `copy` is shallow-merged over the base config and the `personalization` key is stripped, so
 * components receive the same shape they get today.
 *
 * Anything malformed (non-array rules, rules without matchTypes, non-object copy, or a malformed
 * `answers` gate) is skipped, and when no rule matches the base config is returned unchanged.
 * `enabled` is stripped from rule copy: module visibility stays owned by the redesign variables
 * and withModuleGuard, never by a personalization rule.
 */
export function applyPersonalization<T extends Record<string, unknown>> (
  config: T,
  context: PersonalizationContext
): PersonalizationResult<T> {
  const { personalization, ...base } = config as T & { personalization?: unknown }
  const baseConfig = base as unknown as T

  if (!Array.isArray(personalization)) {
    return { config: baseConfig, matchedRuleName: null }
  }

  for (const rule of personalization as PersonalizationRule[]) {
    if (!rule || typeof rule !== 'object' || Array.isArray(rule)) {
      continue
    }
    if (!ruleMatches(rule, context)) {
      continue
    }
    const { copy } = rule
    if (!copy || typeof copy !== 'object' || Array.isArray(copy)) {
      continue
    }
    const copyFields = { ...(copy as Record<string, unknown>) }
    delete copyFields.enabled
    const name = typeof rule.name === 'string' && rule.name.trim() ? rule.name.trim() : 'unnamed'
    return { config: { ...baseConfig, ...copyFields }, matchedRuleName: name }
  }

  return { config: baseConfig, matchedRuleName: null }
}
